import { toast, Toaster, ToastBar } from 'react-hot-toast';

/** Simple close icon for toast dismiss button */
function CloseIcon({ className }: { readonly className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/**
 * ToastProvider component provides global toast notifications using react-hot-toast.
 *
 * Features:
 * - Position: top-right, offset below app header so toasts don't cover the topbar
 * - Shorter duration: default 2.5s, success 2s, error 3.5s
 * - Close button on each toast (except loading) for force dismiss
 * - Dark mode support with Tailwind classes
 * - Accessibility: ARIA labels and focus management via react-hot-toast
 */
export function ToastProvider() {
  return (
    <Toaster
      position="top-right"
      containerStyle={{
        top: '3.5rem', // Below sticky header (h-12 = 3rem) so toasts don't cover topbar
      }}
      toastOptions={{
        duration: 2500,
        className: 'bg-gray-700 dark:bg-gray-800 text-white',
        success: {
          duration: 2000,
          className: 'bg-emerald-500 dark:bg-emerald-600 text-white',
          iconTheme: {
            primary: '#10b981',
            secondary: '#ffffff',
          },
        },
        error: {
          duration: 3500,
          className: 'bg-red-500 dark:bg-red-600 text-white',
          iconTheme: {
            primary: '#ef4444',
            secondary: '#ffffff',
          },
        },
      }}
    >
      {t => (
        <ToastBar toast={t}>
          {({ icon, message }) => (
            <>
              {icon}
              <span className="flex-1">{message}</span>
              {t.type !== 'loading' && (
                <button
                  type="button"
                  onClick={() => toast.dismiss(t.id)}
                  className="ml-2 p-1 rounded hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                  aria-label="Close"
                >
                  <CloseIcon className="text-white" />
                </button>
              )}
            </>
          )}
        </ToastBar>
      )}
    </Toaster>
  );
}
